import { Form, Input, Select, Typography, Radio } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

const { Text } = Typography

const formatPhoneNumber = (phoneNumber: string) => {
  // If the phone number is already formatted, return it as is
  if (phoneNumber.includes('(') && phoneNumber.includes(')')) {
    return phoneNumber
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '')

  // Format the number as (XXX) - XXX - XXXX
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) - ${match[2]} - ${match[3]}`
  }
  return phoneNumber
}

const formatPhoneNumberForInput = (value: string) => {
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '')

  // Format as user types
  if (cleaned.length <= 3) {
    return cleaned
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) - ${cleaned.slice(3)}`
  } else {
    return `(${cleaned.slice(0, 3)}) - ${cleaned.slice(3, 6)} - ${cleaned.slice(
      6,
      10
    )}`
  }
}

interface FormFieldProps {
  name: string
  label: string
  required?: boolean
  isEditing: boolean
  value?: string
  prefixIcon?: IconProp
  type?: 'text' | 'email' | 'phone' | 'select'
  rules?: any[]
  componentType?: 'input' | 'select' | 'radio'
  options?: { label: string; value: string }[]
  placeholder?: string
  commonClassName?: string
}

export const FormField = ({
  name,
  label,
  required = false,
  isEditing,
  value,
  prefixIcon,
  type = 'text',
  rules = [],
  componentType = 'input',
  options = [],
  placeholder,
  commonClassName,
}: FormFieldProps) => {
  const displayValue =
    type === 'phone' && value ? formatPhoneNumber(value) : value

  // Determine if we should apply text capitalization
  const shouldCapitalize =
    type !== 'email' && type !== 'phone' && componentType !== 'select'

  return (
    <Form.Item
      label={<span className='font-bold'>{label}</span>}
      name={name}
      required={required}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      rules={[
        ...(required
          ? [
              {
                required: true,
                message: `Please enter your ${label.toLowerCase()}`,
              },
            ]
          : []),
        ...rules,
      ]}
      hasFeedback
      className='w-full'
      getValueFromEvent={(e) => {
        if (type === 'phone' && e?.target?.value) {
          // Return only the numbers for storage
          return e.target.value.replace(/\D/g, '')
        }
        if (componentType === 'select') {
          return e // For Select component, the value is passed directly
        }
        return e?.target?.value
      }}
    >
      {isEditing ? (
        componentType === 'select' ? (
          <Select
            className={`${commonClassName} custom-state-select`}
            placeholder={placeholder || `Select ${label}`}
            showSearch
            allowClear
            options={options}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                return
              }
            }}
            listHeight={256}
            virtual={false}
            optionFilterProp='label'
            optionLabelProp='label'
            notFoundContent={null}
            defaultActiveFirstOption={true}
          />
        ) : componentType === 'radio' ? (
          <Radio.Group>
            {options.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        ) : (
          <Input
            prefix={
              prefixIcon && (
                <FontAwesomeIcon icon={prefixIcon} className='text-gray-500' />
              )
            }
            type={type}
            placeholder={type === 'phone' ? '(XXX) - XXX - XXXX' : undefined}
            aria-label={`${label} input field`}
            data-testid={`${name}-input`}
            onChange={(e) => {
              if (type === 'phone') {
                const formatted = formatPhoneNumberForInput(e.target.value)
                e.target.value = formatted
              }
            }}
          />
        )
      ) : (
        <Text
          style={shouldCapitalize ? { textTransform: 'capitalize' } : undefined}
        >
          {prefixIcon && (
            <FontAwesomeIcon icon={prefixIcon} className='text-gray-500 mr-2' />
          )}
          {displayValue || '-'}
        </Text>
      )}
    </Form.Item>
  )
}
